<template>
  <article>
    <activity-stage>
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" class="one-and-more" s>
        <div class="steps-container" v-if="currentRound.steps">
          <div class="step left-side">
            <img
              alt="Left side image"
              :src="
                stepLeftIsFinished
                  ? stepLeft.answerImage?.image?.src
                  : stepLeft.monochromeImage?.image?.src
              "
            />

            <text-display-list text-size="small" class="left-side type-line">
              <li>
                <blinking-cursor-text
                  :text="stepLeft.answer"
                  :text-step="0"
                  :roundId="progress.roundIndex"
                >
                </blinking-cursor-text>
              </li>
            </text-display-list>
          </div>

          <div
            class="step right-side"
            :style="{ visibility: rightSideIsVisible ? 'visible' : 'hidden' }"
          >
            <img
              alt="Right side image"
              :src="
                stepRightIsFinished
                  ? stepRight.answerImage?.image?.src
                  : stepRight.monochromeImage?.image?.src
              "
            />

            <text-display-list text-size="small" class="right-side type-line">
              <li>
                <blinking-cursor-text
                  :text="stepRight.answer"
                  :text-step="1"
                  :roundId="progress.roundIndex"
                >
                </blinking-cursor-text>
              </li>
            </text-display-list>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText,
    TextDisplayList
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'One and More',
      stepRightIsFinished: false,
      stepLeftIsFinished: false
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    stepLeft() {
      return this.currentRound.steps?.[0];
    },
    stepRight() {
      return this.currentRound.steps?.[1];
    },
    rightSideIsVisible() {
      return this.progress.stepIndex === 1;
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds();
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },
    async loadImages() {
      const { rounds } = this.activityData;
      rounds.forEach(({ steps }) =>
        steps.forEach(({ activityImage }) => this.queueImage(activityImage?.image?.src))
      );

      return this.downloadImages();
    },
    async loadSounds() {
      const { rounds } = this.activityData;
      rounds.forEach(({ steps }) => {
        steps.forEach(({ answerAudio }) => {
          if (!answerAudio) return;
          this.queueAudio(answerAudio?.audio?.src);
        });
      });

      const cache = await this.downloadAudio();

      return rounds.map(({ steps }) =>
        steps.map(({ answerAudio }) => cache.get(answerAudio?.audio?.src))
      );
    },
    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.one-and-more', 0.5, { css: { opacity: 1 }, delay: 1 });
      });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.finishStep();
      }

      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    finishStep() {
      this.stepLeftIsFinished = true;
      this.stepRightIsFinished = false;
      if (this.rightSideIsVisible) {
        this.stepRightIsFinished = true;
      }
      TweenMax.delayedCall(2, () => {
        if (this.rightSideIsVisible) {
          TweenMax.to('.one-and-more', 0.5, {
            css: { opacity: 0 },
            delay: 2,
            onComplete: () => {
              this.stepLeftIsFinished = false;
              this.setupNewRound();
              TweenMax.to('.one-and-more', 0.5, { opacity: 1 });
            }
          });
        } else {
          this.completeStep();
          this.setCurrentStep();
        }
      });

      TweenMax.delayedCall(0.1, () => {
        const { answerAudio } = this.currentRound.steps[this.progress.stepIndex];
        if (answerAudio) {
          this.sndBellAndAfterPlayOtherHowl(
            this.sfx[this.progress.roundIndex][this.progress.stepIndex]
          );
        } else {
          this.sndBell();
        }
      });
    },

    handleRoundEnd() {
      if (this.isEndOfActivity) {
        TweenMax.delayedCall(2, this.completeActivity);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

img {
  height: auto;
  width: 100%;
}

.one-and-more {
  opacity: 0;
  .overlay {
    position: absolute;
    top: 0;
    left: 0;
  }

  .steps-container {
    display: flex;
    justify-content: space-around;
    margin: 0px 32px;

    ul {
      list-style: none;
      margin-top: auto;
      margin-left: -40px;
    }

    .step {
      display: inline-block;
      width: 329px;
      position: relative;

      .type-line {
        font-size: 30px;

        &.left-side,
        &.right-side {
          text-align: center;
        }
      }
    }
  }
}
</style>
